/* ==========================================================================
   Kubota engine-scoped part search  (#161)
   Consumed by: snippets/kubota-scoped-search.liquid

   Once a customer knows their engine, site-wide search is the wrong tool — it
   returns parts for every other engine. This filters within ONE collection.

   WHY THE WHOLE COLLECTION IS FETCHED, rather than filtering the rendered page:
   the engine sections paginate at 48 while the largest collection (V3800) holds
   1,011 parts, so a filter over the DOM would see 4.7% of it and silently answer
   "no results" for most real queries. /collections/<handle>/products.json returns
   250 per page, so a full index is 1 request for the median engine (125 parts)
   and 5 for the largest.

   WHY NOT SERVER-SIDE: measured against the live store 2026-07-31 — storefront
   search does not index tags (q=V2403 -> 0 results), there is no `tag:` operator,
   and filter.p.tag is inert because only custom.parts_group and availability are
   enabled in Search & Discovery. See the snippet header.

   Degrades to the site-wide search form if fetch fails. Never leaves a dead box.
   ========================================================================== */
(function () {
  'use strict';

  var PAGE_SIZE = 250;   /* products.json maximum */
  var MAX_PAGES = 12;    /* runaway guard: 3,000 parts, ~3x the largest engine */
  var MAX_ROWS  = 60;    /* rendered rows before we ask for a narrower query */
  var MIN_CHARS = 2;
  var DEBOUNCE  = 150;

  var GLYPH = '<svg class="keng-row-glyph" viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
    '<path fill="currentColor" d="M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8Zm0 6a2 2 0 1 1 0-4 2 2 0 0 1 0 4Zm9-2 -1.6-1.2.3-2-2-.5-.7-1.9-1.9.7-1.7-1.2-1.7 1.2-1.9-.7-.7 1.9-2 .5.3 2L3 12l1.6 1.2-.3 2 2 .5.7 1.9 1.9-.7 1.7 1.2 1.7-1.2 1.9.7.7-1.9 2-.5-.3-2L21 12Z"/></svg>';

  /* ---------------------------------------------------------------- matching */

  function normalize(s) {
    return String(s == null ? '' : s).toLowerCase().replace(/\s+/g, ' ').trim();
  }

  function alnum(s) {
    return String(s == null ? '' : s).toLowerCase().replace(/[^a-z0-9]+/g, '');
  }

  function tokenize(q) {
    var raw = normalize(q);
    if (!raw) return [];
    return raw.split(' ').filter(Boolean).map(function (t) {
      return { raw: t, alnum: alnum(t) };
    });
  }

  /* A token matches on the printed text (part number WITH its hyphen, plus the
     part name), or on the punctuation-stripped part number. The second rule is
     what makes "15531-72422", "15531 72422" and "1553172422" all find the same
     part — Kubota numbers are hyphenated and customers type them three ways.
     Guarded at 3+ alphanumerics so a stray "1" cannot match the whole catalog.
     All tokens must match, so "water pump" narrows rather than widens. */
  function matches(item, tokens) {
    for (var i = 0; i < tokens.length; i++) {
      var t = tokens[i];
      var hit = item.text.indexOf(t.raw) !== -1;
      if (!hit && t.alnum.length >= 3) hit = item.numKey.indexOf(t.alnum) !== -1;
      if (!hit) return false;
    }
    return true;
  }

  function search(index, q) {
    var tokens = tokenize(q);
    if (!tokens.length) return [];
    var out = [];
    for (var i = 0; i < index.length; i++) {
      if (matches(index[i], tokens)) out.push(index[i]);
    }
    return out;
  }

  /* Mirrors how main-collection-kubota-engine.liquid builds a row: number from
     the variant SKU, name from the last "|" segment, and the real
     NoImageAvailable_*.png asset treated as no image. */
  function toItem(p) {
    var v = (p.variants && p.variants[0]) || {};
    var sku = v.sku || '';
    var title = p.title || '';
    var segs = title.split('|');
    var head = segs.length > 1 ? segs[0].trim() : '';
    var name = segs[segs.length - 1].trim();

    var img = '';
    if (p.images && p.images.length && p.images[0].src) {
      if (p.images[0].src.indexOf('NoImageAvailable') === -1) img = p.images[0].src;
    }

    return {
      title: title,
      num: sku,
      name: name,
      url: '/products/' + p.handle,
      price: v.price == null ? 0 : parseFloat(v.price) || 0,
      img: img,
      text: normalize(title + ' ' + sku),
      /* '/' separator so a query cannot bridge the SKU into the title's number */
      numKey: alnum(sku) + '/' + alnum(head)
    };
  }

  /* ---------------------------------------------------------------- fetching */

  function fetchPage(handle, page) {
    var url = '/collections/' + encodeURIComponent(handle) +
              '/products.json?limit=' + PAGE_SIZE + '&page=' + page;
    return fetch(url, { headers: { 'Accept': 'application/json' } })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(function (d) { return (d && d.products) || []; });
  }

  function loadIndex(handle, count) {
    var key = 'keng:idx:' + handle;
    try {
      var cached = sessionStorage.getItem(key);
      if (cached) return Promise.resolve(JSON.parse(cached));
    } catch (e) { /* private mode / quota — just refetch */ }

    var pages = Math.max(1, Math.min(MAX_PAGES, Math.ceil((count || PAGE_SIZE) / PAGE_SIZE)));
    var reqs = [];
    for (var i = 1; i <= pages; i++) reqs.push(fetchPage(handle, i));

    return Promise.all(reqs).then(function (batches) {
      var all = [];
      batches.forEach(function (b) { all = all.concat(b); });

      /* all_products_count can lag the real total. If the last page came back
         full, keep walking until one comes back short. */
      function more(page, last) {
        if (last.length < PAGE_SIZE || page > MAX_PAGES) return Promise.resolve(all);
        return fetchPage(handle, page).then(function (b) {
          all = all.concat(b);
          return more(page + 1, b);
        });
      }
      return more(pages + 1, batches.length ? batches[batches.length - 1] : []);
    }).then(function (all) {
      var index = all.map(toItem);
      try { sessionStorage.setItem(key, JSON.stringify(index)); } catch (e) { /* over quota */ }
      return index;
    });
  }

  /* --------------------------------------------------------------- rendering */

  function money(n) {
    try {
      return '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    } catch (e) {
      return '$' + n.toFixed(2);
    }
  }

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  function rowEl(item) {
    var a = el('a', 'keng-row');
    a.href = item.url;

    var thumb = el('span', 'keng-row-thumb');
    if (item.img) {
      var im = document.createElement('img');
      im.src = item.img + (item.img.indexOf('?') === -1 ? '?' : '&') + 'width=120';
      im.alt = item.title;
      im.loading = 'lazy';
      im.width = 60;
      im.height = 60;
      thumb.appendChild(im);
    } else {
      thumb.innerHTML = GLYPH;
    }
    a.appendChild(thumb);

    var main = el('span', 'keng-row-main');
    if (item.num) {
      main.appendChild(el('span', 'keng-row-num', item.num));
      main.appendChild(el('span', 'keng-row-name', item.name));
    } else {
      main.appendChild(el('span', 'keng-row-num', item.name));
    }
    a.appendChild(main);

    var price = el('span', 'keng-row-price');
    if (item.price > 0) price.textContent = money(item.price);
    else price.appendChild(el('span', 'keng-row-call', 'Call'));
    a.appendChild(price);

    var arrow = el('span', 'keng-row-arrow');
    arrow.setAttribute('aria-hidden', 'true');
    arrow.textContent = '→';
    a.appendChild(arrow);

    return a;
  }

  /* -------------------------------------------------------------------- init */

  function init(root) {
    var form      = root.querySelector('[data-keng-form]');
    var input     = root.querySelector('[data-keng-input]');
    var statusEl  = root.querySelector('[data-keng-status]');
    var resultsEl = root.querySelector('[data-keng-results]');
    if (!form || !input || !statusEl || !resultsEl) return;
    if (!window.fetch || !window.Promise) return;   /* leave the plain form alone */

    var handle    = root.getAttribute('data-collection-handle');
    var count     = parseInt(root.getAttribute('data-collection-count'), 10) || 0;
    /* Label only, never label + a noun: the engine pages pass "V3800" but the system
       pages pass "Cooling System", so "No Cooling System system parts match" is one
       word away on every message that appends a noun. */
    var label     = root.getAttribute('data-scope-label') || 'these';
    var searchUrl = root.getAttribute('data-search-url') || '/search';
    if (!handle) return;

    var listEl  = document.querySelector('[data-keng-list]');
    var chipsEl = document.querySelector('[data-keng-chips]');

    var index = null, loading = null, timer = null;

    function ensureIndex() {
      if (index) return Promise.resolve(index);
      if (loading) return loading;
      loading = loadIndex(handle, count).then(function (i) {
        index = i;
        return i;
      }, function (err) {
        loading = null;
        throw err;
      });
      return loading;
    }

    /* Restore the server-rendered page. */
    function showBrowse() {
      resultsEl.hidden = true;
      resultsEl.textContent = '';
      statusEl.hidden = true;
      statusEl.textContent = '';
      if (listEl) listEl.hidden = false;
      if (chipsEl) chipsEl.hidden = false;
    }

    function hideBrowse() {
      if (listEl) listEl.hidden = true;
      /* The chip bar is hidden while searching on purpose: the search covers the
         whole engine, so a lit "Cooling System" chip would misdescribe the results. */
      if (chipsEl) chipsEl.hidden = true;
    }

    function siteWideLink(q) {
      var a = el('a', 'keng-sr-all', 'Search all Kubota parts →');
      a.href = searchUrl + '?q=' + encodeURIComponent(q) + '&type=product';
      return a;
    }

    function clearBtn() {
      var b = el('button', 'keng-sr-clear', 'Clear');
      b.type = 'button';
      b.addEventListener('click', function () {
        input.value = '';
        showBrowse();
        input.focus();
      });
      return b;
    }

    function setStatus(nodes) {
      statusEl.textContent = '';
      statusEl.hidden = false;
      nodes.forEach(function (n) {
        statusEl.appendChild(typeof n === 'string' ? document.createTextNode(n) : n);
      });
    }

    function render(q) {
      var hits = search(index, q);
      hideBrowse();

      resultsEl.textContent = '';
      if (!hits.length) {
        resultsEl.hidden = true;
        setStatus([
          'No ' + label + ' parts match “' + q + '”. ',
          siteWideLink(q), ' ', clearBtn()
        ]);
        return;
      }

      var shown = hits.slice(0, MAX_ROWS);
      var list = el('div', 'keng-list');
      shown.forEach(function (item) { list.appendChild(rowEl(item)); });
      resultsEl.appendChild(list);

      if (hits.length > MAX_ROWS) {
        resultsEl.appendChild(
          el('p', 'keng-sr-more',
             'Showing the first ' + MAX_ROWS + ' of ' + hits.length + ' matches — add another word to narrow it down.')
        );
      }
      resultsEl.hidden = false;

      var strong = el('strong', null, String(hits.length));
      /* Verb agrees with the count: "1 part matches", "79 parts match". */
      setStatus([
        strong, ' ' + label + (hits.length === 1 ? ' part matches “' : ' parts match “') + q + '”. ',
        clearBtn(), ' ', siteWideLink(q)
      ]);
    }

    function fail(q) {
      /* Index unavailable — hand the query to site-wide search rather than
         pretending the engine has no matching parts. */
      window.location.href = searchUrl + '?q=' + encodeURIComponent(q) + '&type=product';
    }

    function run(q) {
      setStatus(['Searching all ' + (count || '') + ' ' + label + ' parts…']);
      ensureIndex().then(function () { render(q); }, function () { fail(q); });
    }

    input.addEventListener('input', function () {
      var q = input.value.trim();
      if (timer) clearTimeout(timer);
      if (q.length < MIN_CHARS) { showBrowse(); return; }
      timer = setTimeout(function () { run(q); }, DEBOUNCE);
    });

    /* Warm the index while the customer is still typing the first characters. */
    input.addEventListener('focus', function () {
      ensureIndex().catch(function () { /* handled on submit */ });
    }, { once: true });

    input.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { input.value = ''; showBrowse(); }
    });

    form.addEventListener('submit', function (e) {
      var q = input.value.trim();
      if (!q) return;                 /* let the empty form behave natively */
      e.preventDefault();
      if (timer) clearTimeout(timer);
      run(q);
    });
  }

  function boot() {
    var roots = document.querySelectorAll('[data-keng-search]');
    for (var i = 0; i < roots.length; i++) init(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Exposed so artifacts/tooling/verify-engine-scoped-search.js can unit-test the
     matcher in Node without a browser. */
  window.AlpineEngineSearch = {
    normalize: normalize,
    alnum: alnum,
    tokenize: tokenize,
    matches: matches,
    search: search,
    toItem: toItem
  };
})();